import type { ThreadSummary } from '@core/tellent-assistant-types';

interface MastraChunk {
  type: string;
  payload?: Record<string, unknown>;
}

export interface DevAssistantFixture {
  id: string;
  label: string;
  description: string;
  threads?: readonly ThreadSummary[];
  threadMessages?: Record<string, readonly unknown[]>;
  responseChunks?: readonly MastraChunk[];
  responseError?: { message: string };
}

const finishChunks: readonly MastraChunk[] = [
  { type: 'step-finish', payload: { stepResult: { reason: 'stop', isContinued: false } } },
  { type: 'finish', payload: { stepResult: { reason: 'stop', isContinued: false } } },
];

const userTextResponse = (text: string): readonly MastraChunk[] => [
  { type: 'start', payload: {} },
  { type: 'step-start', payload: {} },
  { type: 'text-start', payload: { id: 't1' } },
  { type: 'text-delta', payload: { id: 't1', text } },
  { type: 'text-end', payload: { id: 't1' } },
  ...finishChunks,
];

const subAgentToolOutput = (
  parentToolCallId: string,
  parentToolName: string,
  innerChunks: readonly MastraChunk[],
): MastraChunk[] =>
  innerChunks.map(inner => ({
    type: 'tool-output',
    payload: { toolCallId: parentToolCallId, toolName: parentToolName, output: inner },
  }));

const streamingToolCallChunks = (): readonly MastraChunk[] => {
  const id = 'tc-stream';
  const toolName = 'presentCandidates';
  const args = JSON.stringify({ items: [{ id: 'c1', name: 'Jane' }] });
  const deltas: MastraChunk[] = Array.from(args).map(ch => ({
    type: 'tool-call-delta',
    payload: { toolCallId: id, toolName, argsTextDelta: ch },
  }));
  return [
    { type: 'start', payload: { messageId: 'm-stream' } },
    { type: 'step-start', payload: {} },
    { type: 'tool-call-input-streaming-start', payload: { toolCallId: id, toolName } },
    ...deltas,
    { type: 'tool-call-input-streaming-end', payload: { toolCallId: id } },
    { type: 'tool-call', payload: { toolCallId: id, toolName, args: JSON.parse(args) } },
    { type: 'tool-result', payload: { toolCallId: id, toolName, result: 'Rendered.' } },
    ...finishChunks,
  ];
};

const toolErrorChunks = (): readonly MastraChunk[] => {
  const id = 'tc-err';
  return [
    { type: 'start', payload: { messageId: 'm-err' } },
    { type: 'step-start', payload: {} },
    {
      type: 'tool-call',
      payload: { toolCallId: id, toolName: 'presentCandidates', args: { items: [] } },
    },
    {
      type: 'tool-result',
      payload: {
        toolCallId: id,
        toolName: 'presentCandidates',
        result: { error: 'Vector store unreachable' },
        isError: true,
      },
    },
    ...finishChunks,
  ];
};

const subAgentStreamingChunks = (): readonly MastraChunk[] => {
  const parent = 'parent-tc';
  const parentTool = 'agent-analyticsAgent';
  return [
    { type: 'start', payload: { messageId: 'm-parent' } },
    { type: 'step-start', payload: {} },
    { type: 'tool-call', payload: { toolCallId: parent, toolName: parentTool, args: {} } },
    ...subAgentToolOutput(parent, parentTool, [
      { type: 'start', payload: { messageId: 'm-sub' } },
      { type: 'text-start', payload: { id: 'sub-text' } },
      { type: 'text-delta', payload: { id: 'sub-text', text: 'Querying analytics…\n\n' } },
      { type: 'text-delta', payload: { id: 'sub-text', text: 'Found 12 hires this quarter.' } },
      { type: 'text-end', payload: { id: 'sub-text' } },
    ]),
    {
      type: 'tool-result',
      payload: {
        toolCallId: parent,
        toolName: parentTool,
        result: {
          text: 'Found 12 hires this quarter.',
          subAgentThreadId: 't-sub',
          subAgentResourceId: 'tellentAssistant-analyticsAgent',
          subAgentToolResults: [],
        },
      },
    },
    ...finishChunks,
  ];
};

const multiStepInProgressChunks = (): readonly MastraChunk[] => {
  const t1 = 'tc-1';
  const t2 = 'tc-2';
  const tool1Name = 'queryDb';
  const tool2Name = 'aggregateByTeam';
  const t2Args = JSON.stringify({ metric: 'hires', groupBy: 'team' });
  const t2Deltas: MastraChunk[] = Array.from(t2Args).map(ch => ({
    type: 'tool-call-delta',
    payload: { toolCallId: t2, toolName: tool2Name, argsTextDelta: ch },
  }));
  return [
    { type: 'start', payload: { messageId: 'm-multi' } },
    { type: 'step-start', payload: {} },
    {
      type: 'reasoning-delta',
      payload: { text: 'I need to look up the hiring data for this quarter…' },
    },
    {
      type: 'tool-call',
      payload: {
        toolCallId: t1,
        toolName: tool1Name,
        args: { sql: 'SELECT count(*) FROM hires WHERE quarter = current_quarter' },
      },
    },
    {
      type: 'tool-result',
      payload: { toolCallId: t1, toolName: tool1Name, result: { rows: [{ count: 12 }] } },
    },
    { type: 'step-finish', payload: { stepResult: { reason: 'tool-call', isContinued: true } } },
    { type: 'step-start', payload: {} },
    { type: 'reasoning-delta', payload: { text: 'Now let me break those hires down by team.' } },
    {
      type: 'tool-call-input-streaming-start',
      payload: { toolCallId: t2, toolName: tool2Name },
    },
    ...t2Deltas,
    // No tool-call final / tool-result / finish — second tool stays in-flight.
  ];
};

const subAgentReloadMessage = {
  id: 'a1',
  role: 'assistant',
  content: {
    format: 2,
    parts: [
      {
        type: 'tool-invocation',
        toolInvocation: {
          state: 'result',
          toolCallId: 'parent-tc',
          toolName: 'agent-analyticsAgent',
          args: { prompt: 'Analyse hires' },
          result: {
            text: 'Found 12 hires this quarter across 4 teams.',
            subAgentThreadId: 't-sub-inner',
            subAgentResourceId: 'tellentAssistant-analyticsAgent',
            subAgentToolResults: [
              {
                toolCallId: 'sub-tc-1',
                toolName: 'queryDb',
                args: { sql: 'SELECT count(*) FROM hires' },
                result: { rows: [{ count: 12 }] },
              },
              {
                toolCallId: 'sub-tc-2',
                toolName: 'aggregateByTeam',
                args: { metric: 'hires' },
                result: { engineering: 5, sales: 3, design: 2, ops: 2 },
              },
            ],
          },
        },
      },
    ],
    content: '',
  },
};

const sourcesCitationChunks = (): readonly MastraChunk[] => {
  const id = 'tc-sources';
  const toolName = 'presentSources';
  const args = {
    items: [
      { id: 'doc-1', title: 'Office Rules', url: 'https://x.test/1', provider: 'notion' },
      { id: 'doc-2', title: 'Travel Policy', url: 'https://x.test/2', provider: 'documents' },
    ],
  };
  return [
    { type: 'start', payload: { messageId: 'm-sources' } },
    { type: 'step-start', payload: {} },
    { type: 'tool-call', payload: { toolCallId: id, toolName, args } },
    {
      type: 'tool-result',
      payload: { toolCallId: id, toolName, result: { componentId: 'presentSources_dev00001' } },
    },
    { type: 'text-start', payload: { id: 't1' } },
    {
      type: 'text-delta',
      payload: {
        id: 't1',
        text: 'You can park in garage P2 on workdays after registering your plate.\n\n{{component:presentSources_dev00001}}',
      },
    },
    { type: 'text-end', payload: { id: 't1' } },
    ...finishChunks,
  ];
};

export const DEV_ASSISTANT_FIXTURES: readonly DevAssistantFixture[] = [
  {
    id: 'empty-state',
    label: 'Empty state',
    description: 'Greeting + conversation starters, no thread loaded.',
  },
  {
    id: 'reload-text-conversation',
    label: 'Reload — plain text exchange',
    description: 'Switch to a seeded thread; rehydrated user + assistant text.',
    threads: [
      {
        id: 't-old',
        title: 'Old chat',
        updatedAt: new Date('2026-04-17T10:00:00Z'),
        pinned: false,
      },
    ],
    threadMessages: {
      't-old': [
        { id: 'u1', role: 'user', content: 'What did I do yesterday?' },
        { id: 'a1', role: 'assistant', content: 'You closed three Acme tickets.' },
      ],
    },
  },
  {
    id: 'streaming-text',
    label: 'Streaming — assistant text',
    description: 'Send any message; assistant streams a multi-chunk reply.',
    responseChunks: userTextResponse('Sure, here are the latest hires this quarter…'),
  },
  {
    id: 'streaming-tool-call-args',
    label: 'Streaming — tool-call partial args',
    description: 'Tool-call args drip in via tool-call-delta then finalise.',
    responseChunks: streamingToolCallChunks(),
  },
  {
    id: 'tool-call-error',
    label: 'Tool error pill',
    description: 'Tool result with status: error — surfaces the error pill.',
    responseChunks: toolErrorChunks(),
  },
  {
    id: 'multi-step-in-progress',
    label: 'Multi-step — reasoning → tool → reasoning → tool (in progress)',
    description: 'Two reasoning + tool cycles; second tool mid-stream (partial args).',
    responseChunks: multiStepInProgressChunks(),
  },
  {
    id: 'sub-agent-streaming',
    label: 'Sub-agent — streaming text',
    description: 'Parent agent-X tool-call + tool-output chunks streaming sub-agent text live.',
    responseChunks: subAgentStreamingChunks(),
  },
  {
    id: 'sub-agent-reload',
    label: 'Sub-agent — reload (synthesized)',
    description: 'Stored agent-X tool-invocation with rolled-up subAgentToolResults.',
    threads: [
      {
        id: 't-sub',
        title: 'Analytics chat',
        updatedAt: new Date('2026-04-17T10:00:00Z'),
        pinned: false,
      },
    ],
    threadMessages: {
      't-sub': [{ id: 'u1', role: 'user', content: 'Analyse hires' }, subAgentReloadMessage],
    },
  },
  {
    id: 'http-500',
    label: 'HTTP 500 from stream',
    description: 'Stream returns 500. Run finalises; no assistant text.',
    responseError: { message: 'HTTP 500: Internal Server Error' },
  },
  {
    id: 'sources-citation',
    label: 'Sources — presentSources cards',
    description: 'Answer grounded in the knowledge base; presentSources cards at the end.',
    responseChunks: sourcesCitationChunks(),
  },
];
